import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { sendContact } from "@/lib/api";
import { INTERESTS } from "@/lib/content";

export default function ContactForm({ theme = "a", primary = "navy" }) {
  const b = theme === "b";
  const empty = { name: "", email: "", interest: INTERESTS[0], message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const inputCls = b
    ? "w-full bg-transparent border-b border-forest/25 py-3 text-forest placeholder:text-forest/35 focus:border-clay outline-none transition-colors rounded-none"
    : "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy placeholder:text-navy/40 outline-none focus:border-blueA transition-colors";
  const labelCls = `block text-xs uppercase tracking-[0.14em] mb-1.5 ${
    b ? "text-forest/60" : "text-navy/60"
  }`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await sendContact(form);
      toast.success("Message sent — I'll reply personally, soon.");
      setForm(empty);
    } catch {
      toast.error("Couldn't send just now — please email hello@wellandtruly.fit.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className={b ? "font-bodyb" : "font-bodya"} data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="cf-name">Your name</label>
          <input
            id="cf-name"
            data-testid="contact-name-input"
            className={inputCls}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name"
            required
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-email">Email</label>
          <input
            id="cf-email"
            type="email"
            data-testid="contact-email-input"
            className={inputCls}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@example.com"
            required
          />
        </div>
      </div>
      <div className="mt-5">
        <label className={labelCls} htmlFor="cf-interest">I'm interested in</label>
        <select
          id="cf-interest"
          data-testid="contact-interest-select"
          className={`${inputCls} cursor-pointer`}
          value={form.interest}
          onChange={(e) => setForm({ ...form, interest: e.target.value })}
        >
          {INTERESTS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div className="mt-5">
        <label className={labelCls} htmlFor="cf-message">What brings you here?</label>
        <textarea
          id="cf-message"
          data-testid="contact-message-input"
          className={`${inputCls} resize-none`}
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="A line or two about you, and what you'd like to change…"
          required
        />
      </div>
      <button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={sending}
        className={`mt-7 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
          b
            ? "bg-forest text-cream hover:bg-forest/90"
            : primary === "royal"
              ? "bg-royal text-cream hover:bg-royaldeep"
              : "bg-navy text-lime hover:bg-navy/90"
        }`}
      >
        {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {sending ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
